(function (root) {
  'use strict';

  var COUPON_CODE_KEY = 'ev:klaviyo-coupon-code:v1';
  var PENDING_CODE = '·················';
  var INVISIBLE_OR_SPACE = /[\s\u200B-\u200D\uFEFF]/g;

  function extractCouponCode(text) {
    var match = String(text || '').match(
      /\bW[\s\u200B-\u200D\uFEFF]*E[\s\u200B-\u200D\uFEFF]*L[\s\u200B-\u200D\uFEFF]*C[\s\u200B-\u200D\uFEFF]*O[\s\u200B-\u200D\uFEFF]*M[\s\u200B-\u200D\uFEFF]*E[\s\u200B-\u200D\uFEFF]*1[\s\u200B-\u200D\uFEFF]*0(?:-[A-Z0-9]+|[A-Z0-9]{8})\b/i
    );
    return match ? match[0].replace(INVISIBLE_OR_SPACE, '').toUpperCase() : null;
  }

  function findCouponCode(doc, selector) {
    if (!doc.querySelector && !doc.querySelectorAll) return null;
    var candidates = [];

    function addCandidates(candidateSelector) {
      if (doc.querySelectorAll) {
        var matches = doc.querySelectorAll(candidateSelector);
        for (var index = 0; index < matches.length; index += 1) {
          if (candidates.indexOf(matches[index]) === -1) {
            candidates.push(matches[index]);
          }
        }
        return;
      }

      var match = doc.querySelector(candidateSelector);
      if (match && candidates.indexOf(match) === -1) candidates.push(match);
    }

    addCandidates(selector);
    addCandidates('[data-testid="POPUP"]');
    if (doc.body && candidates.indexOf(doc.body) === -1) {
      candidates.push(doc.body);
    }

    var couponCode = null;
    candidates.forEach(function (couponBlock) {
      var nextCode = extractCouponCode(couponBlock.textContent);
      if (!nextCode) return;
      if (!couponCode || nextCode.length >= couponCode.length) {
        couponCode = nextCode;
      }
    });
    return couponCode;
  }

  function maskCode(code, progress) {
    var charactersToReveal = Math.floor(code.length * progress);
    return code.split('').map(function (character, index) {
      if (character === '-') return '-';
      return index < charactersToReveal ? character : '·';
    }).join('');
  }

  function createCouponSuccessController(options) {
    var durationMs = options.durationMs || 1000;
    var currentCode = null;
    var observer = null;
    var pending = false;
    var retryTimer = null;
    var retryAttempts = 0;
    var fallbackShown = false;
    var resolved = false;
    var view = options.view;
    var viewIsOpen = false;

    function openView() {
      if (viewIsOpen) return;
      viewIsOpen = view.open() !== false;
    }

    function reveal(code) {
      var startedAt = options.now();
      openView();
      view.setCopied(false);

      if (options.reducedMotion) {
        view.renderCode(code);
        view.setReady(true);
        return;
      }

      view.renderCode(maskCode(code, 0));
      view.setReady(false);

      function frame(timestamp) {
        var progress = Math.min(1, (timestamp - startedAt) / durationMs);
        view.renderCode(maskCode(code, progress));
        if (progress < 1) {
          options.requestFrame(frame);
          return;
        }
        view.renderCode(code);
        view.setReady(true);
      }

      options.requestFrame(frame);
    }

    function clearRetry() {
      if (retryTimer === null) return;
      if (options.cancelRetry) options.cancelRetry(retryTimer);
      retryTimer = null;
    }

    function scheduleCouponRetry() {
      if (resolved || retryTimer !== null || !options.scheduleRetry) return;
      retryTimer = options.scheduleRetry(function () {
        retryTimer = null;
        retryAttempts += 1;
        resolveCoupon();
        if (
          !resolved
          && options.fallbackCode
          && retryAttempts >= (options.maxCouponRetries || 30)
          && !fallbackShown
        ) {
          showFallback(options.fallbackCode);
        }
        if (!resolved) scheduleCouponRetry();
      }, options.retryDelayMs || 100);
    }

    function showFallback(code) {
      fallbackShown = true;
      currentCode = code;
      options.storage.setItem(COUPON_CODE_KEY, code);
      reveal(code);
    }

    function completeCoupon(code) {
      resolved = true;
      pending = false;
      clearRetry();
      if (observer) observer.disconnect();
      currentCode = code;
      options.storage.setItem(COUPON_CODE_KEY, code);
      reveal(code);
    }

    function resolveCoupon() {
      if (resolved) return;
      var code = options.findCouponCode();
      if (code) completeCoupon(code);
    }

    function handleSubmit() {
      resolved = false;
      pending = true;
      retryAttempts = 0;
      fallbackShown = false;
      openView();
      view.renderCode(PENDING_CODE);
      view.setCopied(false);
      view.setReady(false);
      resolveCoupon();
      if (!resolved) {
        observer = options.observeCoupon(resolveCoupon);
        scheduleCouponRetry();
      }
    }

    function openSaved() {
      var savedCode = options.storage.getItem(COUPON_CODE_KEY);
      if (!savedCode) {
        if (!pending) return false;
        openView();
        view.renderCode(PENDING_CODE);
        view.setCopied(false);
        view.setReady(false);
        return true;
      }

      currentCode = savedCode;
      openView();
      view.renderCode(savedCode);
      view.setCopied(false);
      view.setReady(true);
      return true;
    }

    function copy() {
      if (!currentCode) return Promise.resolve();
      return options.copyText(currentCode).then(function () {
        view.setCopied(true);
      });
    }

    function close() {
      viewIsOpen = false;
      view.close();
    }

    view.bind({ close: close, copy: copy });

    return { handleSubmit: handleSubmit, openSaved: openSaved, close: close };
  }

  function createFallbackStorage() {
    var values = {};
    return {
      getItem: function (key) {
        return Object.prototype.hasOwnProperty.call(values, key)
          ? values[key]
          : null;
      },
      setItem: function (key, value) {
        values[key] = String(value);
      },
    };
  }

  function copyInBrowser(doc, win, text) {
    if (win.navigator && win.navigator.clipboard) {
      return win.navigator.clipboard.writeText(text);
    }

    var area = doc.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.opacity = '0';
    doc.body.appendChild(area);
    area.select();
    doc.execCommand('copy');
    area.remove();
    return Promise.resolve();
  }

  function installBrowserCouponSuccess(options) {
    var doc = options.document;
    var win = options.window;
    var selector = options.selector || '.ev-welcome-coupon';
    var overlay = doc.querySelector('[data-klaviyo-coupon-success]');
    if (!overlay) return null;

    var codeOutput = overlay.querySelector('[data-klaviyo-coupon-code]');
    var copyButton = overlay.querySelector('[data-klaviyo-coupon-copy]');
    var closeButton = overlay.querySelector('.klaviyo-coupon-success__close');
    var continueButtons = overlay.querySelectorAll(
      '[data-klaviyo-coupon-continue]'
    );
    var ready = false;
    var copied = false;

    function renderCopyButton() {
      copyButton.disabled = !ready;
      copyButton.setAttribute('data-copied', String(copied));
      copyButton.textContent = !ready
        ? 'PŘIPRAVUJI KÓD…'
        : copied ? 'ZKOPÍROVÁNO ✓' : 'KOPÍROVAT KÓD';
    }

    var view = {
      bind: function (handlers) {
        copyButton.addEventListener('click', function () {
          return handlers.copy();
        });
        Array.prototype.forEach.call(continueButtons, function (button) {
          button.addEventListener('click', handlers.close);
        });
        doc.addEventListener('keydown', function (event) {
          if (event.key === 'Escape' && !overlay.hidden) handlers.close();
        });
      },
      close: function () {
        overlay.hidden = true;
        doc.body.classList.remove('klaviyo-coupon-success-open');
        win.dispatchEvent(new win.CustomEvent(
          'ev:klaviyo-coupon-success-closed'
        ));
      },
      open: function () {
        // Coupon resolution/retry may finish after the shopper enters the cart.
        // Keep the accepted code, but never reopen its overlay during checkout.
        if (doc.documentElement.classList.contains('klaviyo-purchase-active')
          || doc.documentElement.classList.contains('klaviyo-offer-paused')) return false;
        overlay.hidden = false;
        doc.body.classList.add('klaviyo-coupon-success-open');
        doc.documentElement.classList.add(
          'klaviyo-coupon-success-native-hidden'
        );
        if (closeButton && closeButton.focus) closeButton.focus();
      },
      renderCode: function (code) {
        codeOutput.textContent = code;
      },
      setCopied: function (nextCopied) {
        copied = nextCopied;
        renderCopyButton();
      },
      setReady: function (nextReady) {
        ready = nextReady;
        renderCopyButton();
      },
    };

    return createCouponSuccessController({
      copyText: options.copyText || function (text) {
        return copyInBrowser(doc, win, text);
      },
      durationMs: 1000,
      findCouponCode: function () {
        return findCouponCode(doc, selector);
      },
      now: function () {
        return win.performance && win.performance.now
          ? win.performance.now()
          : Date.now();
      },
      observeCoupon: function (callback) {
        var observer = new win.MutationObserver(callback);
        observer.observe(doc.body, {
          attributes: true,
          characterData: true,
          childList: true,
          subtree: true,
        });
        return observer;
      },
      reducedMotion: win.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches,
      requestFrame: win.requestAnimationFrame.bind(win),
      cancelRetry: win.clearTimeout
        ? win.clearTimeout.bind(win)
        : null,
      fallbackCode: 'WELCOME10',
      scheduleRetry: win.setTimeout
        ? win.setTimeout.bind(win)
        : null,
      storage: options.storage || createFallbackStorage(),
      view: view,
    });
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      createCouponSuccessController: createCouponSuccessController,
      findCouponCode: findCouponCode,
      installBrowserCouponSuccess: installBrowserCouponSuccess,
    };
  }

  root.EVKlaviyoCouponSuccess = {
    createCouponSuccessController: createCouponSuccessController,
    findCouponCode: findCouponCode,
    installBrowserCouponSuccess: installBrowserCouponSuccess,
  };

  if (root.document) {
    var script = root.document.currentScript;
    var selector = script && script.getAttribute('data-coupon-selector');
    var storage;
    try {
      storage = root.sessionStorage;
    } catch (error) {
      storage = createFallbackStorage();
    }
    root.EVKlaviyoCouponSuccessController = installBrowserCouponSuccess({
      document: root.document,
      selector: selector || '.ev-welcome-coupon',
      storage: storage,
      window: root,
    });
  }
})(typeof window === 'undefined' ? globalThis : window);
